'use client';

import { ChevronRight, type LucideIcon } from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '../../components/ui/sidebar';
import { Link } from 'react-router';
import { useMatchRoutes } from '../../routing';
import { useTranslation } from 'react-i18next';

export function NavMain({
  items,
  label,
}: {
  items: {
    title: string;
    url: string;
    icon: LucideIcon;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
  label?: string;
}) {
  const { isCurrentPath } = useMatchRoutes();
  const { t } = useTranslation();

  const isItemActive = (
    itemUrl: string,
    subItems?: { title: string; url: string }[]
  ) => {
    if (isCurrentPath(itemUrl)) return true;
    if (subItems) {
      return subItems.some((subItem) => isCurrentPath(subItem.url));
    }
    return false;
  };

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="p-2 text-xs font-bold uppercase text-primary-100">
        {t('sidebar.platform')}
      </SidebarGroupLabel>
      <SidebarMenu className="flex flex-col gap-4">
        {items.map((item) => {
          const isActive = isItemActive(item.url, item.items);

          return (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={item.isActive || isActive}
            >
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  className={`font-semibold transition-all duration-200 rounded-md px-3 py-5 text-base group-data-[collapsible=icon]:justify-center ${
                    isActive
                      ? 'bg-gradient-main text-white hover:bg-gradient-main hover:text-white'
                      : 'hover:bg-gradient-main hover:text-white text-white'
                  }`}
                >
                  <Link
                    to={item.url}
                    className="group-data-[collapsible=icon]:justify-center"
                  >
                    <item.icon className="shrink-0" />
                    <span className="group-data-[collapsible=icon]:hidden">
                      {item.title}
                    </span>
                  </Link>
                </SidebarMenuButton>
                {item.items?.length ? (
                  <>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuAction className="data-[state=open]:rotate-90">
                        <ChevronRight />
                        <span className="sr-only">{t('common.toggle')}</span>
                      </SidebarMenuAction>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items?.map((subItem) => {
                          const isSubItemActive = isCurrentPath(subItem.url);
                          return (
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton
                                asChild
                                className={
                                  isSubItemActive
                                    ? 'bg-blue-50 text-blue-700'
                                    : ''
                                }
                              >
                                <Link to={subItem.url}>
                                  <span>{subItem.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </>
                ) : null}
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
